// Role-based access control for the admin panel.
//
// Operations users log into the same admin panel as admins and share its
// session keys (AdminUserObj / AdminToken), so every admin screen and
// HttpconfigAdmin call keeps working unchanged. What separates them is
// user_type:
//
//   * Finance is closed to them - the sidebar tab and the dashboard card are
//     greyed out and the paths behind them are refused.
//   * Order Medicine is theirs in full - every action on those screens works.
//   * Everything else is read-only - the pages open and the data shows, but the
//     create / update / delete affordances are hidden (Header puts the
//     READ_ONLY_BODY_CLASS on <body>, admin.css does the hiding) and the pages
//     that exist to write something are refused outright.
//
// Adding a role is a change to ROLE_PERMISSIONS and the lists below.

//  Only two roles run this panel. A legacy 'admin' account existed once; it
//  has been promoted to super_admin in the DB rather than kept as a third
//  role in code.
export const ROLE_SUPER_ADMIN = "super_admin";
export const ROLE_OPERATIONS = "operations";

export const ADMIN_LOGIN_PATH = "/admin/login";
export const OPERATIONS_LOGIN_PATH = "/operations/login";

export const ADMIN_LANDING = "/admin";
//  Operations sees the same dashboard as an admin - what differs is what the
//  screens behind it let them do, not where they land.
export const OPERATIONS_LANDING = ADMIN_LANDING;

//  Class Header hangs on <body> while a restricted role is on a read-only
//  screen. admin.css keys the hiding rules off it.
export const READ_ONLY_BODY_CLASS = "ops-readonly";

//  Pattern suffixes used by every list below:
//    (none)  exact match
//    /*      the path itself and anything below it
//    /+      only paths below it (an :id or sub-page is required)
//  Patterns are lowercase; the path is lowercased before matching.

//  Reachable whoever is (or is not) logged in.
const PUBLIC_PATHS = [ADMIN_LOGIN_PATH, OPERATIONS_LOGIN_PATH];

//  The Order Medicine section, which operations owns outright - every button on
//  these screens works for them exactly as it does for an admin. Mirrors the
//  "Order Medicine" menu in sidebar.js, plus the pages those screens open.
export const OPERATIONS_FULL_ACCESS_PATHS = [
  "/admin/vieworders", // Orders
  "/admin/viewproductrequestom", // Product Request
  "/admin/viewproducts", // product list
  "/admin/editproducts/*", // UPDATE a product
  "/admin/uploadimages/*", // product image upload
  "/admin/uploadproductimage",
  "/admin/uploadmasterproducts", // UPLOAD
  "/admin/stockupload", // STOCK UPLOAD
  "/admin/product-approvals/*", // APPROVE (with inline update and reject)
  "/admin/viewretailers", // pharmacy list
  "/admin/createretailers/*", // CREATE / UPDATE a pharmacy
  "/admin/viewretailerswalletlist",
  "/admin/createretailerswalletlist/*",
  "/admin/pharmacywalletorderreport",
  "/admin/walletreport",
];

//  Closed to operations altogether. Finance is somebody else's ledger, and
//  handing out panel access is the Super Admin's alone (the server enforces the
//  second one on /api/Operations - this only keeps the UI honest).
export const OPERATIONS_DENIED_PATHS = [
  "/admin/viewfinanceusers",
  "/admin/createfinanceuser/*",
  "/admin/viewoperationsusers",
  "/admin/createoperationsuser/*",
];

//  Pages whose whole purpose is to write something. Outside Order Medicine a
//  read-only role has no business opening one, so they are refused even when
//  the link that leads there has been hidden.
const WRITE_PAGE_PATTERNS = [
  /^\/admin\/creat/, // Create… and the Creat… spellings (Creatcancellationreasons)
  /^\/admin\/edit/,
  /^\/admin\/set/, // SetAssessmentQuestions / Response / Score
  /^\/admin\/update/,
  /^\/admin\/adminupdate/, // AdminUpdateBanners
  /^\/admin\/upload/,
  /^\/admin\/imageuploads/,
  /^\/admin\/uploadcommonimages/,
  /^\/admin\/patientregistration/,
  /^\/admin\/categoryregistration/,
];

//  Screens the pattern above would wrongly refuse: their route is named like
//  a write page but the component is really a list + inline-create combo
//  (Createviewrelationships fetches and renders a table on mount - there is
//  no separate "Viewrelationships" route to send a read-only role to
//  instead). Allowed to open, same as anything else outside Order Medicine:
//  the page itself still renders read-only, since this list is deliberately
//  NOT added to OPERATIONS_FULL_ACCESS_PATHS.
const READ_ONLY_NAME_EXCEPTIONS = [
  "/admin/createviewrelationships",
];

//  paths: restricted false means the role is unrestricted inside the panel.
export const ROLE_PERMISSIONS = {
  [ROLE_SUPER_ADMIN]: {
    landing: ADMIN_LANDING,
    loginPath: ADMIN_LOGIN_PATH,
    restricted: false,
  },
  [ROLE_OPERATIONS]: {
    landing: OPERATIONS_LANDING,
    loginPath: OPERATIONS_LOGIN_PATH,
    restricted: true,
  },
};

//  Roles allowed to create internal users and change their role or status.
//  Mirrors verifyRole.ROLE_MANAGERS on the server, which is what actually
//  enforces it - this only decides whether the menu entry is worth showing.
export const ROLE_MANAGERS = [ROLE_SUPER_ADMIN];

//  Sessions saved before roles existed carry no user_type, so they fall back
//  to super_admin and keep the full panel.
const permissionsFor = (role) =>
  ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS[ROLE_SUPER_ADMIN];

//  Reads the logged in admin-panel user
export function getAdminUser() {
  try {
    return JSON.parse(localStorage.getItem("AdminUserObj"));
  } catch (error) {
    return null;
  }
}

//  Role of the logged in user.
export function getAdminRole() {
  const user = getAdminUser();
  return user && user.user_type ? user.user_type : ROLE_SUPER_ADMIN;
}

export function isOperations() {
  return getAdminRole() === ROLE_OPERATIONS;
}

//  Whether this role may manage internal users and their roles
export function canManageInternalUsers(role) {
  return ROLE_MANAGERS.indexOf(role || getAdminRole()) !== -1;
}

//  Login page this user should be sent back to when the session ends
export function loginPathForRole(role) {
  return permissionsFor(role).loginPath;
}

//  First page this role should see after logging in
export function landingForRole(role) {
  return permissionsFor(role).landing;
}

//  A restricted role is held to the rules above; the rest of the panel is open
//  to everyone else.
export function isRestrictedRole(role) {
  return permissionsFor(role).restricted;
}

function matchesPattern(path, pattern) {
  const allowed = pattern.toLowerCase();
  if (allowed.endsWith("/*")) {
    const base = allowed.slice(0, -2);
    return path === base || path.startsWith(base + "/");
  }
  if (allowed.endsWith("/+")) {
    const base = allowed.slice(0, -2);
    return path.startsWith(base + "/") && path.length > base.length + 1;
  }
  return path === allowed;
}

const matchesAny = (path, patterns) =>
  patterns.some((pattern) => matchesPattern(path, pattern));

//  "/Admin/ViewProducts/?x=1" -> "/admin/viewproducts"
function normalize(pathname) {
  if (!pathname) return "";
  let path = pathname.split("?")[0].split("#")[0].toLowerCase();
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
}

//  Whether the given role is allowed to open the given path
export function isPathAllowedForRole(role, pathname) {
  if (!isRestrictedRole(role)) return true;
  if (!pathname) return false;

  const path = normalize(pathname);
  if (matchesAny(path, PUBLIC_PATHS)) return true;
  if (matchesAny(path, OPERATIONS_DENIED_PATHS)) return false;
  //  Order Medicine first: those screens include create and edit pages that
  //  the write-page rule below would otherwise refuse.
  if (matchesAny(path, OPERATIONS_FULL_ACCESS_PATHS)) return true;
  if (matchesAny(path, READ_ONLY_NAME_EXCEPTIONS)) return true;
  return !WRITE_PAGE_PATTERNS.some((pattern) => pattern.test(path));
}

//  Whether an operations user is allowed to open the given path
export function isPathAllowedForOperations(pathname) {
  return isPathAllowedForRole(ROLE_OPERATIONS, pathname);
}

//  Whether this role may look at the given path but not change anything on it.
//  Everything a restricted role can open is read-only except Order Medicine.
export function isReadOnlyPathForRole(role, pathname) {
  if (!isRestrictedRole(role)) return false;
  return !matchesAny(normalize(pathname), OPERATIONS_FULL_ACCESS_PATHS);
}

//  Same question for whoever is logged in right now
export function isReadOnlyPath(pathname) {
  return isReadOnlyPathForRole(getAdminRole(), pathname);
}

//  Finance is closed to operations. The sidebar tab and the dashboard card use
//  this to grey themselves out instead of linking anywhere.
export function canAccessFinance(role) {
  return isPathAllowedForRole(role || getAdminRole(), "/admin/ViewFinanceUsers");
}

//  Every panel role now works out of /admin, so this is the plain admin prefix.
//  Screens that navigate between shared pages keep calling it rather than
//  hardcoding the prefix, which is what let operations move here without them
//  changing. e.g. adminPath("product-approvals") -> "/admin/product-approvals".
export function adminPath(subPath) {
  return subPath ? `${ADMIN_LANDING}/${subPath}` : ADMIN_LANDING;
}

//  Human-readable name for the header, e.g. "super_admin" -> "Super Admin"
export function roleLabel(role) {
  return String(role || ROLE_SUPER_ADMIN)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
